import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useReactNativeMessage from '../../../hooks/useReactNativeMessage';

/**
 * MobilePaymentBase component.
 * Base component that wraps payment components with common mobile payment functionality.
 * Uses Function as Child Component pattern for better transparency and flexibility.
 *
 * @param {object} props - Base props and child function
 * @param {Function} props.children - Function that receives enhanced props
 * @param {object} props.errors - Error object from the payment process
 * @param {Function} props.clearErrors - Function to clear errors
 * @param {Function} props.getPurchase - Function to fetch purchase data
 * @param {boolean} props.fetchingPayment - Whether payment is currently being fetched
 * @param {object} props.purchase - Purchase object containing payment details
 * @param {Function} props.resetPaymentCardError - Function to reset payment card errors
 * @param {boolean} props.cardErrorOccured - Whether a card error has occurred
 * @param {string} props.token - Purchase token from URL
 * @returns {React.ReactElement} The wrapped payment component
 */
const MobilePaymentBase = ({ children, ...props }) => {
  const {
    errors,
    clearErrors,
    getPurchase,
    fetchingPayment,
    purchase,
    resetPaymentCardError,
    cardErrorOccured,
  } = props;

  const { availablePayments = [], isUpdating, loaded } = purchase;

  const [isLoading, setIsLoading] = useState(isUpdating || fetchingPayment || !loaded);
  const isCreditCardAvailable = availablePayments.some((type) => type === 'credit_card');
  const RNMessage = useReactNativeMessage();

  // Message handling functionality
  const setupMessageListener = useCallback(
    (handler) => {
      RNMessage.listen(handler);
    },
    [RNMessage],
  );

  const sendHeightMessage = useCallback(() => {
    const height = document.querySelector('div.l-container')?.clientHeight;
    if (height) {
      RNMessage.post({ height });
    }
  }, [RNMessage]);

  const sendErrorMessage = useCallback(
    (error) => {
      RNMessage.post({ error });
    },
    [RNMessage],
  );

  // Update height when loading state changes
  useEffect(() => {
    if (!isLoading) {
      sendHeightMessage();
    }
    RNMessage.post({ loading: isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, sendHeightMessage]);

  // Update loading state when dependencies change
  useEffect(() => {
    setIsLoading(isUpdating || fetchingPayment || !loaded);
  }, [loaded, isUpdating, fetchingPayment]);

  // Initialize component
  useEffect(() => {
    if (!loaded) {
      getPurchase();
    }

    if (typeof resetPaymentCardError === 'function') {
      resetPaymentCardError();
    }

    return () => {
      if (props.resetInstallmentsPlan) {
        props.resetInstallmentsPlan();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify mobile app if credit card is not available
  useEffect(() => {
    if (loaded && !isCreditCardAvailable) {
      sendErrorMessage('Credit card not available');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditCardAvailable, loaded]);

  // Handle card errors
  useEffect(() => {
    if (cardErrorOccured) {
      sendErrorMessage('Credit card payment error');
    }
  }, [cardErrorOccured, sendErrorMessage]);

  // Handle general errors
  useEffect(() => {
    if (errors && errors.code !== 0) {
      sendErrorMessage(errors.message);
      clearErrors();
    }
  }, [clearErrors, errors, sendErrorMessage]);

  return (
    <div className="l-mobile-purchase">
      <div className="l-container">
        {children({
          ...props, // Pass all original props
          RNMessage,
          sendErrorMessage,
          setupMessageListener,
          sendHeightMessage,
          isLoading: isLoading || !loaded || !isCreditCardAvailable,
          isCreditCardAvailable,
        })}
      </div>
    </div>
  );
};

MobilePaymentBase.propTypes = {
  children: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  clearErrors: PropTypes.func.isRequired,
  getPurchase: PropTypes.func.isRequired,
  purchase: PropTypes.shape({
    isUpdating: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    availablePayments: PropTypes.array,
    total: PropTypes.number.isRequired,
  }),
  token: PropTypes.string.isRequired, // Token como prop directa
  paymentId: PropTypes.number,
  fetchingPayment: PropTypes.bool.isRequired,
  resetInstallmentsPlan: PropTypes.func.isRequired,
  cardErrorOccured: PropTypes.bool,
  resetPaymentCardError: PropTypes.func,
};

export default MobilePaymentBase;
